"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Nav } from "@/components/nav";
import { fetchApi } from "@/lib/api-fetch";
import { apiFetchPath } from "@/lib/api-fetch-path";
import { BLOG_DEFAULT_COVER_SRC } from "@/lib/blog-cover";
import { sanitizeBlogHtmlClient } from "@mot-cards/blog-sanitize/client";
import { PUBLIC_BLOG_AUTHOR_NAME } from "@mot-cards/blog-utils";

type Post = {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  bodyHtml: string;
  coverImageUrl: string | null;
  publishedAt: string | null;
  readingTimeMinutes: number;
  metaTitle: string | null;
  metaDescription: string | null;
  metaNoindex: boolean;
  author: { displayName: string; username: string; avatarUrl: string | null };
};

type Related = Post;

export function BlogPostClient({ slug }: { slug: string }) {
  const [post, setPost] = useState<Post | null>(null);
  const [related, setRelated] = useState<Related[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      setLoading(true);
      setError(null);
      setPost(null);
      setRelated([]);
      try {
        const res = await fetchApi(apiFetchPath(`/api/v1/blog/posts/${encodeURIComponent(slug)}`));
        if (res.status === 404) {
          if (!cancelled) setError("notfound");
          return;
        }
        if (!res.ok) throw new Error("Could not load post");
        const data = (await res.json()) as { post: Post; relatedPosts: Related[] };
        if (cancelled) return;
        setPost(data.post);
        setRelated(data.relatedPosts ?? []);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : "Error");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="flex min-h-screen flex-col bg-[#0f2030]">
        <Nav />
        <main
          id="main-content"
          className="mot-atmosphere flex-1 px-6 py-12 outline-none"
          tabIndex={-1}
          aria-busy="true"
          aria-label="Loading article"
        >
          <p className="text-mot-grey">Loading…</p>
        </main>
      </div>
    );
  }

  if (error === "notfound" || !post) {
    return (
      <div className="flex min-h-screen flex-col bg-[#0f2030]">
        <Nav />
        <main
          id="main-content"
          className="mot-atmosphere mx-auto max-w-2xl flex-1 px-6 py-16 outline-none"
          tabIndex={-1}
        >
          <h1 className="font-[family-name:var(--font-archivo-black)] text-2xl font-normal uppercase text-white">
            Post not found
          </h1>
          <Link href="/blog" className="mt-4 inline-block text-mot-teal hover:underline">
            ← Back to blog
          </Link>
        </main>
      </div>
    );
  }

  const safeHtml = sanitizeBlogHtmlClient(post.bodyHtml);

  return (
    <div className="flex min-h-screen flex-col bg-[#0f2030]">
      <Nav />
      <main
        id="main-content"
        className="mot-atmosphere mx-auto max-w-3xl flex-1 scroll-mt-4 px-6 py-12 outline-none sm:px-10"
        tabIndex={-1}
      >
        <nav aria-label="Breadcrumb" className="mb-8 text-sm text-mot-grey">
          <Link href="/blog" className="hover:text-mot-teal">
            Blog
          </Link>
          <span className="mx-2">/</span>
          <span className="text-mot-white">{post.title}</span>
        </nav>

        <div className="mb-8 overflow-hidden rounded-2xl border border-dark-border">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={post.coverImageUrl ?? BLOG_DEFAULT_COVER_SRC}
            alt=""
            className="max-h-[420px] w-full object-cover"
          />
        </div>

        <header className="mb-8">
          <p className="text-sm text-mot-grey">
            {post.publishedAt
              ? new Date(post.publishedAt).toLocaleDateString(undefined, {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })
              : ""}{" "}
            · {post.readingTimeMinutes} min read
          </p>
          <h1 className="mt-2 font-[family-name:var(--font-archivo-black)] text-4xl font-normal uppercase tracking-tight text-white">
            {post.title}
          </h1>
          <p className="mt-4 text-mot-grey">
            By <span className="text-mot-white">{PUBLIC_BLOG_AUTHOR_NAME}</span>
          </p>
        </header>

        <div
          className="prose-blog"
          dangerouslySetInnerHTML={{ __html: safeHtml }}
        />

        <footer className="mt-12 border-t border-dark-border pt-8">
          <p className="text-sm text-mot-grey">Written by {PUBLIC_BLOG_AUTHOR_NAME}</p>
          <div className="mt-6 flex flex-wrap gap-3">
            <span className="text-sm text-mot-grey">Share:</span>
            <a
              className="text-sm text-mot-teal hover:underline"
              href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(
                typeof window !== "undefined" ? window.location.href : "",
              )}&text=${encodeURIComponent(post.title)}`}
              target="_blank"
              rel="noopener noreferrer"
            >
              X / Twitter
            </a>
          </div>
        </footer>

        {related.length > 0 ? (
          <section className="mt-12 border-t border-dark-border pt-10">
            <h2 className="text-lg font-semibold text-mot-white">Related posts</h2>
            <ul className="mt-4 space-y-3">
              {related.map((r) => (
                <li key={r.id}>
                  <Link href={`/blog/${r.slug}`} className="text-mot-teal hover:underline">
                    {r.title}
                  </Link>
                </li>
              ))}
            </ul>
          </section>
        ) : null}
      </main>
    </div>
  );
}
